import useVelocityClient from '../../useVelocityClient';
import invariant from 'tiny-invariant';
import { BigNum, PublicKey } from '@velocity-exchange/sdk';
import { useCallback } from 'react';
import useCurrentAuthority from '../../useCurrentAuthority';
import { useSpotMarketsLookup } from '../../spotMarkets/useSpotMarketsLookup';
import {
	createUserAndDepositCollateralBaseIxs,
	MarketId,
	convertLeverageToMarginRatio,
} from '@velocity-exchange/common';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { useGetTxParams } from '../transactions/useGetTxParams';
import { notify } from 'src/utils/notifications';
import useVelocityAccountsStore from 'src/stores/useVelocityAccountsStore';
import {
	DEFAULT_ACCOUNT_NAMES_BY_POOL_ID,
	MAX_COMPUTE_UNITS,
} from 'src/constants/constants';
import { useTxErrorHandler } from '../transactions/useTxErrorHandler';
import { usePrivyWalletContext } from 'src/providers/wallets/PrivyWalletProvider';
import captureException from 'src/utils/captureException';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import { useAccountInitialization } from './useAccountInitialization';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useSponsoredSignVelocityActionWithFallback } from 'src/hooks/wallets/useSponsoredSignVelocityActionWithFallback';
import { TransactionInstruction } from '@solana/web3.js';

interface CreateNewSubaccountProps {
	depositAmount: BigNum;
	spotMarketIndex: number;
	poolId?: number;
	accountName?: string;
	/** Takes the deposit from this subaccount */
	fromSubAccountId?: number;
	maxLeverage?: number | undefined;
	/**
	 * Invoked the moment account creation succeeds, BEFORE the trailing
	 * account-subscription settling sleeps. Callers pass a modal close handler
	 * here so the modal closes immediately on success.
	 */
	onAccountCreated?: () => void;
}

export const useCreateNewSubaccount = () => {
	const velocityClient = useVelocityClient();
	const authority = useCurrentAuthority();
	const spotMarketConfigsLookup = useSpotMarketsLookup();
	const userStatsAccount = useVelocityStore((s) => s.userStatsAccount);
	const isGeoBlocked = useVelocityStore((s) => s.isGeoblocked);
	const countryCode = useVelocityStore((s) => s.countryCode);
	const referrerName = useVelocityStore((s) => s.referrerParam);
	const { embeddedWallet } = usePrivyWalletContext();

	// Store setters and getters
	const getAccountsState = useVelocityAccountsStore((s) => s.get);
	const walletAdapter = useVelocityStore((s) => s.wallet.current.adapter);

	const getTxParams = useGetTxParams();
	const handleTxError = useTxErrorHandler();
	const {
		runAccountInitializationTxAndHandling,
		handleCreateVelocityUserSuccess,
	} = useAccountInitialization();

	const createSubaccountAction = useCallback(
		async (
			wallet: UiEnhancedIWalletV2,
			ixs: TransactionInstruction[],
			spotMarketIndex: number
		) => {
			const marketId = MarketId.createSpotMarket(spotMarketIndex);
			const txParams = getTxParams({
				marketId,
				computeUnits: MAX_COMPUTE_UNITS,
				useSimulatedComputeUnits: false,
			});
			const txSig = await wallet.signAndSendTransaction(ixs, txParams);
			return txSig;
		},
		[getTxParams]
	);

	const wrappedCreateSubaccount = useSponsoredSignVelocityActionWithFallback(
		createSubaccountAction
	);

	const createNewSubaccount = useCallback(
		async (props: CreateNewSubaccountProps): Promise<boolean> => {
			const {
				depositAmount,
				spotMarketIndex,
				poolId,
				accountName,
				fromSubAccountId,
				maxLeverage,
				onAccountCreated,
			} = props;

			const spotMarketConfig = spotMarketConfigsLookup[spotMarketIndex];

			invariant(velocityClient, 'Drift client not found');
			invariant(authority, 'Authority not found');
			invariant(spotMarketConfig, 'Spot market config not found');

			if (isGeoBlocked && countryCode !== 'US') {
				notify({
					type: 'error',
					message: 'Geoblocked',
					description:
						'You are not allowed to use Velocity from a restricted territory',
					lengthMs: 5000,
				});
				return false;
			}
			if (!walletAdapter || !walletAdapter.publicKey) return false;

			const accountsState = getAccountsState();
			const hasAccountForPool = Object.values(accountsState.accounts).some(
				(account) => account?.client?.getUserAccountOrThrow().poolId === poolId
			);

			const subAccountIdToInit =
				userStatsAccount?.numberOfSubAccountsCreated ?? 0;
			const newAccountName = accountName
				? accountName
				: !hasAccountForPool && !!poolId
					? DEFAULT_ACCOUNT_NAMES_BY_POOL_ID[poolId]
					: undefined;
			const customMaxMarginRatio = convertLeverageToMarginRatio(
				maxLeverage ?? 0
			);
			// referrerParam is only set in the store after on-chain validation
			// (see handleReferralPageLoad), so its presence means a valid referral.
			// The escrow + referrerInfo wiring is handled inside
			// createUserAndDepositCollateralBaseIxs via the `referral` param.
			const isReferred = !!referrerName;

			const toastId = `create_new_subaccount_${Date.now()}`;
			notify({
				id: toastId,
				type: 'awaiting_wallet_confirmation',
				message: 'Creating new subaccount.',
				showUntilCancelled: true,
			});

			const executeTransaction = async () => {
				try {
					const { ixs } = await createUserAndDepositCollateralBaseIxs({
						velocityClient: velocityClient,
						amount: depositAmount.val,
						spotMarketConfig,
						authority,
						userStatsAccount,
						referral: referrerName ? { name: referrerName } : undefined,
						accountName: newAccountName,
						poolId,
						fromSubAccountId,
						customMaxMarginRatio,
						delegate: embeddedWallet
							? new PublicKey(embeddedWallet.address)
							: undefined,
					});

					const txSig = await wrappedCreateSubaccount(ixs, spotMarketIndex);

					return { txSig };
				} catch (error: unknown) {
					handleTxError(error, toastId);
					return undefined;
				}
			};

			const customSuccessHandler = async (
				userIdToInit: number,
				successHandlerToastId?: string,
				switchAccount?: boolean
			) => {
				// The success signal (`onAccountCreated`), settling delay, and
				// `userAccountNotInitialized` reset are all handled once in
				// `runAccountInitializationTxAndHandling`'s success step, so this handler
				// only needs to run the user-facing success flow.
				await handleCreateVelocityUserSuccess({
					userIdToInit,
					toastId: successHandlerToastId,
					switchAccount,
					customMessages: {
						successMessage: 'New subaccount created.',
					},
				});
			};

			const result = await runAccountInitializationTxAndHandling({
				isReferred,
				velocityClient,
				userIdToInit: subAccountIdToInit,
				toastId,
				runInitializationTransaction: async () => {
					try {
						const txResult = await executeTransaction();
						if (!txResult) return undefined;

						return { txSig: txResult.txSig };
					} catch (e: any) {
						captureException(e);
						TransactionErrorHandler.handleError({
							error: e,
							toastId,
							wallet: walletAdapter,
						});
						return undefined;
					}
				},
				poolId,
				customMessages: {
					successMessage: 'New subaccount created.',
					initializingMessage: 'Deposit successful',
				},
				onSuccess: customSuccessHandler,
				onAccountCreated,
			});

			return result;
		},
		[
			velocityClient,
			authority,
			spotMarketConfigsLookup,
			userStatsAccount,
			isGeoBlocked,
			countryCode,
			referrerName,
			embeddedWallet,
			walletAdapter,
			getAccountsState,
			handleTxError,
			handleCreateVelocityUserSuccess,
			runAccountInitializationTxAndHandling,
			wrappedCreateSubaccount,
		]
	);

	return createNewSubaccount;
};
